import * as React from 'react';
import algoliasearch from 'algoliasearch/lite';
import { InstantSearch } from 'react-instantsearch-hooks-web';
import styled from 'styled-components';
import Footer from './Footer';
import Header from './Header';
import { algoliaIndexName, pages } from '../../../config';
import SearchContent from '../SearchContent';
import SearchContext from '../../contexts/SearchContext';
import { updateScholarshipStorageValue } from '../../utils/frontend';

const isBrowser = typeof window !== 'undefined';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  overflow-x: hidden;

  .electron-app & {
    padding-bottom: 150px; // to make space for the player as we hide the footer
  }
`;

const Main = styled.main<{ $isSearchOpen: boolean }>`
  display: ${({ $isSearchOpen }) => ($isSearchOpen ? 'none' : 'flex')};
  flex-direction: column;
  align-items: stretch;
  flex: 1;

  > * {
    width: 100%;
  }
`;

interface Props {
  children: React.ReactNode;
  hideHeader?: boolean;
  hideFooter?: boolean;
  headerHasMarginBottom?: boolean;
  footerHasMarginTop?: boolean;
}

export default function PageLayout({
  children,
  hideHeader = false,
  hideFooter = false,
  headerHasMarginBottom = true,
  footerHasMarginTop = true,
}: Props) {
  const { isSearchOpen, setIsSearchOpen, setIsShowingFilters } = React.useContext(SearchContext);
  React.useEffect(() => {
    // if user is out of the scholarship request flow (scholarship, sign in, sign up pages)
    // reset the scholarship storage value
    if (
      isBrowser &&
      ![
        pages.signIn,
        pages.signUp,
        pages.signInWithEmail,
        pages.checkSignInEmail,
        pages.becomeAMember,
      ].includes(window.location.pathname)
    ) {
      updateScholarshipStorageValue('reset');
    }
  }, []);

  const searchClient = React.useMemo(
    () =>
      algoliasearch(
        process.env.GATSBY_ALGOLIA_APP_ID || '',
        process.env.GATSBY_ALGOLIA_SEARCH_KEY || '',
      ),
    [],
  );

  React.useEffect(() => {
    setIsSearchOpen(false);
    setIsShowingFilters(false);
  }, [setIsSearchOpen, setIsShowingFilters]);

  return (
    <Wrapper>
      {hideHeader ? null : <Header hasMarginBottom={headerHasMarginBottom} />}
      <Main $isSearchOpen={isSearchOpen} id="main-container">
        {children}
      </Main>
      {hideFooter ? null : <Footer hasMarginTop={footerHasMarginTop} />}
      {/* Note: anything inside InstantSearch is not rendered as static HTML in page source */}
      <InstantSearch searchClient={searchClient} indexName={algoliaIndexName}>
        <SearchContent />
      </InstantSearch>
    </Wrapper>
  );
}
